// src/components/Authorizations/Filters.js

import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Input,
  Select,
  Button,
  DatePicker,
  message,
  Space,
} from "antd";
import {
  SearchOutlined,
  PlusCircleOutlined,
  UploadOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import PropTypes from "prop-types";
import "./Filters.css";

const { Option } = Select;
const { RangePicker } = DatePicker;

const Filters = ({
  filter,
  onFilterChange,
  filterStatus,
  onFilterStatusChange,
  filterAdditionalStatus,
  onFilterAdditionalStatusChange,
  filterAuthType,
  onFilterAuthTypeChange,
  filterEPS,
  onFilterEPSChange,
  epsOptions,
  onOpenCreateForm,
  onOpenBulkUpload,
  filterNoPbs,
  onFilterNoPbsChange,
  filterDateRange,
  onFilterDateRangeChange,
  filterCustomDateRange,
  onFilterCustomDateRangeChange,
  setExportModalVisible,
  hasPermission,
}) => {
  /**
   * Genera las opciones disponibles para el filtro de estado adicional
   * basado en el estado principal seleccionado.
   */
  const getAdditionalStatusOptions = () => {
    if (filterStatus === "Completada") {
      return [
        <Option key="X traslados" value="X traslados">
          X traslados
        </Option>,
        <Option key="X Fecha" value="X Fecha">
          X Fecha
        </Option>,
        <Option key="Con Fallidos" value="Con Fallidos">
          Con Fallidos
        </Option>,
      ];
    } else if (filterStatus === "Cerrada") {
      return [
        <Option key="Sin Traslados" value="Sin Traslados">
          Sin Traslados
        </Option>,
        <Option key="Manualmente" value="Manualmente">
          Manualmente
        </Option>,
      ];
    }
    return [];
  };

  /**
   * Maneja el cambio en el rango de fechas personalizado.
   * Asegura que el rango no exceda los 3 meses.
   */
  const handleCustomDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      const diffMonths = end.diff(start, "month", true);
      if (diffMonths > 3) {
        message.error("El rango no puede exceder los 3 meses");
        return;
      }
      onFilterCustomDateRangeChange(dates);
      onFilterDateRangeChange("");
    } else {
      onFilterCustomDateRangeChange([]);
    }
  };

  /**
   * Estado local para gestionar el valor del campo de búsqueda con debounce.
   */
  const [searchInput, setSearchInput] = useState(filter);

  /**
   * Sincroniza el estado local `searchInput` con la prop `filter` cuando cambia.
   */
  useEffect(() => {
    setSearchInput(filter);
  }, [filter]);

  /**
   * Implementa el debounce: llama a `onFilterChange` solo después de 500 ms
   * de inactividad en la escritura.
   */
  useEffect(() => {
    const handler = setTimeout(() => {
      onFilterChange(searchInput);
    }, 500); // 500 ms de retraso

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput, onFilterChange]);

  /**
   * Maneja el cambio en el campo de búsqueda.
   * Actualiza el estado local `searchInput` en lugar de llamar directamente a `onFilterChange`.
   */
  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  return (
    <div className="filters-container">
      {/* Fila única que contiene filtros y botones */}
      <Row
        gutter={[12, 12]}
        wrap={true}
        align="middle"
        className="filters-row"
        justify="space-between"
      >
        {/* Columna para los filtros */}
        <Col xs={24} lg={20}>
          <Row gutter={[12, 12]} wrap={true} align="middle">
            {/* Filtro de Búsqueda General */}
            <Col xs={24} sm={12} md={6} lg={4}>
              <Input
                placeholder="Buscar..."
                value={searchInput}
                onChange={handleSearchChange}
                suffix={<SearchOutlined />}
                allowClear
                className="filters-input"
                size="small"
              />
            </Col>

            {/* Filtro de Estado Principal */}
            <Col xs={24} sm={12} md={6} lg={3}>
              <Select
                placeholder="Estado"
                value={filterStatus || undefined}
                onChange={(value) => {
                  onFilterStatusChange(value);
                  onFilterAdditionalStatusChange(undefined);
                }}
                className="filters-select"
                allowClear
                size="middle"
              >
                <Option value="Activa">Activa</Option>
                <Option value="Suspendida">Suspendida</Option>
                <Option value="Completada">Completada</Option>
                <Option value="Cerrada">Cerrada</Option>
                <Option value="Anulada">Anulada</Option>
              </Select>
            </Col>

            {/* Filtro de Estado Adicional */}
            <Col xs={24} sm={12} md={6} lg={3}>
              <Select
                placeholder="Estado Adicional"
                value={filterAdditionalStatus || undefined}
                onChange={onFilterAdditionalStatusChange}
                className="filters-select"
                allowClear
                disabled={
                  !filterStatus || getAdditionalStatusOptions().length === 0
                }
                size="middle"
              >
                {getAdditionalStatusOptions()}
              </Select>
            </Col>

            {/* Filtro de Tipo de Autorización (AUT/PRE/Ambas) */}
            <Col xs={24} sm={12} md={6} lg={3}>
              <Select
                placeholder="AUT/PRE/Ambas"
                value={filterAuthType || undefined}
                allowClear
                onChange={onFilterAuthTypeChange}
                className="filters-select"
                size="middle"
              >
                <Option value="autorizacion">AUT</Option>
                <Option value="pre_autorizacion">PRE</Option>
                <Option value="ambas">Ambas</Option>
              </Select>
            </Col>

            {/* Filtro de EPS */}
            <Col xs={24} sm={12} md={6} lg={4}>
              <Select
                showSearch
                placeholder="EPS"
                value={filterEPS || undefined}
                onChange={onFilterEPSChange}
                className="filters-select"
                allowClear
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                size="middle"
              >
                {epsOptions.map((eps) => (
                  <Option key={eps.id} value={eps.nombre_cliente}>
                    {eps.nombre_cliente}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* Filtro PBS/NOPBS */}
            <Col xs={24} sm={12} md={6} lg={3}>
              <Select
                placeholder="PBS/NOPBS"
                value={filterNoPbs || undefined}
                onChange={onFilterNoPbsChange}
                className="filters-select"
                allowClear
                size="middle"
              >
                <Option value="PBS">PBS</Option>
                <Option value="NOPBS">NOPBS</Option>
              </Select>
            </Col>

            {/* Filtro de Rango de Fechas Predeterminado */}
            <Col xs={24} sm={12} md={6} lg={3}>
              <Select
                placeholder="Rango"
                value={filterDateRange || undefined}
                onChange={(value) => {
                  onFilterDateRangeChange(value);
                  onFilterCustomDateRangeChange([]);
                }}
                className="filters-select"
                allowClear
                size="middle"
              >
                <Option value="current_week">Semana Actual</Option>
                <Option value="previous_week">Semana Anterior</Option>
                <Option value="current_month">Mes Actual</Option>
                <Option value="previous_month">Mes Anterior</Option>
              </Select>
            </Col>

            {/* Filtro de Rango de Fechas Personalizado */}
            <Col xs={24} sm={24} md={12} lg={5}>
              <RangePicker
                value={filterCustomDateRange}
                onChange={handleCustomDateChange}
                allowClear
                format="YYYY-MM-DD"
                className="filters-range-picker"
                placeholder={["Fecha Inicio", "Fecha Fin"]}
                size="middle"
              />
            </Col>
          </Row>
        </Col>

        {/* Columna para los botones */}
        <Col xs={24} lg={4} style={{ textAlign: "right" }}>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {hasPermission("CREAR_A") && onOpenCreateForm && (
              <Button
                type="primary"
                size="large"
                icon={<PlusCircleOutlined />}
                onClick={onOpenCreateForm}
                className="filters-button"
                title="Nueva Autorización"
                block
              >
                Nueva Autorización
              </Button>
            )}

            <Row gutter={[12, 12]} style={{ width: "100%" }}>
            {hasPermission("CREAR_A") && onOpenBulkUpload && (
                <Col span={12}>
                  <Button
                    type="primary"
                    size="large"
                    icon={<UploadOutlined />}
                    onClick={onOpenBulkUpload}
                    className="filters-button"
                    title="Importar Autorizaciones"
                    block
                  />
                </Col>
              )}
              {hasPermission("PREVISUALIZAR_A") && setExportModalVisible && (
                <Col span={12}>
                  <Button
                    type="primary"
                    size="large"
                    icon={<DownloadOutlined />}
                    onClick={() => setExportModalVisible(true)}
                    className="filters-button"
                    style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                    title="Exportar Autorizaciones"
                    block
                  />
                </Col>
              )}
            </Row>
          </Space>
        </Col>
      </Row>
    </div>
  );
};

Filters.propTypes = {
  filter: PropTypes.string.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  filterStatus: PropTypes.string,
  onFilterStatusChange: PropTypes.func.isRequired,
  filterAdditionalStatus: PropTypes.string,
  onFilterAdditionalStatusChange: PropTypes.func.isRequired,
  filterAuthType: PropTypes.string,
  onFilterAuthTypeChange: PropTypes.func.isRequired,
  filterEPS: PropTypes.string,
  onFilterEPSChange: PropTypes.func.isRequired,
  epsOptions: PropTypes.array.isRequired,
  onOpenCreateForm: PropTypes.func.isRequired,
  onOpenBulkUpload: PropTypes.func.isRequired,
  filterNoPbs: PropTypes.string,
  onFilterNoPbsChange: PropTypes.func.isRequired,
  filterDateRange: PropTypes.string,
  onFilterDateRangeChange: PropTypes.func.isRequired,
  filterCustomDateRange: PropTypes.array.isRequired,
  onFilterCustomDateRangeChange: PropTypes.func.isRequired,
  setExportModalVisible: PropTypes.func.isRequired,
  hasPermission: PropTypes.func,
};

export default Filters;
